import {Group, Shape, ShapeGeometry, Vector2,} from 'three';
import {ViewportGizmoControlAction} from './ViewportGizmoControlAction';
import {ViewportGizmoOuterControlMesh} from './ViewportGizmoOuterControlMesh';


/** 组合四向三角按钮和两个视线滚转弧形按钮。 */
export class ViewportGizmoOuterControls extends Group {
  /** 三角按钮中心到导航器中心的距离。 */
  private static readonly TRIANGLE_DISTANCE = 76;

  /** 弧形按钮内半径。 */
  private static readonly ARC_INNER_RADIUS = 68;

  /** 弧形按钮外半径。 */
  private static readonly ARC_OUTER_RADIUS = 73;

  /** 全部可拾取按钮。 */
  private readonly controls: readonly ViewportGizmoOuterControlMesh[];

  /** 当前悬停按钮。 */
  private hoveredControl: ViewportGizmoOuterControlMesh | null = null;

  /** 创建并装配六个外部控制按钮。 */
  public constructor() {
    super();
    this.name = '视口方向外部控件组';
    this.controls = this.createControls();
    this.add(...this.controls);
  }

  /**
   * 获取全部可拾取按钮。
   *
   * @returns 保持创建顺序的按钮只读集合。
   */
  public getControls(): readonly ViewportGizmoOuterControlMesh[] {
    return this.controls;
  }

  /**
   * 设置当前悬停按钮。
   *
   * @param control 当前射线命中按钮；未命中时为 `null`。
   */
  public setHoveredControl(
    control: ViewportGizmoOuterControlMesh | null,
  ): void {
    if (this.hoveredControl === control) {
      return;
    }

    // 先恢复旧按钮，再设置新按钮，保证同一时刻只有一个高亮目标。
    this.hoveredControl?.setHovered(false);
    this.hoveredControl = control;
    this.hoveredControl?.setHovered(true);
  }

  /** 释放全部按钮几何和材质。 */
  public dispose(): void {
    this.setHoveredControl(null);
    for (const control of this.controls) {
      control.dispose();
    }
    this.clear();
  }

  /**
   * 创建四向按钮和两个滚转按钮。
   *
   * @returns 六个外部控制按钮的只读集合。
   */
  private createControls(): readonly ViewportGizmoOuterControlMesh[] {
    const up = this.createTriangleControl(
      ViewportGizmoControlAction.OrbitUp,
      0,
      0,
      ViewportGizmoOuterControls.TRIANGLE_DISTANCE,
    );
    const down = this.createTriangleControl(
      ViewportGizmoControlAction.OrbitDown,
      Math.PI,
      0,
      -ViewportGizmoOuterControls.TRIANGLE_DISTANCE,
    );
    const left = this.createTriangleControl(
      ViewportGizmoControlAction.OrbitLeft,
      Math.PI / 2,
      -ViewportGizmoOuterControls.TRIANGLE_DISTANCE,
      0,
    );
    const right = this.createTriangleControl(
      ViewportGizmoControlAction.OrbitRight,
      -Math.PI / 2,
      ViewportGizmoOuterControls.TRIANGLE_DISTANCE,
      0,
    );
    const counterClockwise = new ViewportGizmoOuterControlMesh(
      this.createArcArrowGeometry(Math.PI / 12, Math.PI / 2.8, true),
      ViewportGizmoControlAction.RollCounterClockwise,
    );
    const clockwise = new ViewportGizmoOuterControlMesh(
      this.createArcArrowGeometry(-Math.PI / 2.8, -Math.PI / 12, false),
      ViewportGizmoControlAction.RollClockwise,
    );

    return [up, down, left, right, counterClockwise, clockwise];
  }

  /**
   * 创建一个指向导航器外侧的三角按钮。
   *
   * @param action 按钮触发动作。
   * @param rotation 按钮绕屏幕 Z 轴旋转弧度。
   * @param x 按钮中心 X 坐标。
   * @param y 按钮中心 Y 坐标。
   * @returns 已定位的三角控制按钮。
   */
  private createTriangleControl(
    action: ViewportGizmoControlAction,
    rotation: number,
    x: number,
    y: number,
  ): ViewportGizmoOuterControlMesh {
    const shape = new Shape()
      .moveTo(0, 6)
      .lineTo(-5, -4)
      .lineTo(5, -4)
      .closePath();
    const control = new ViewportGizmoOuterControlMesh(
      new ShapeGeometry(shape),
      action,
    );
    control.position.set(x, y, 0);
    control.rotation.z = rotation;
    return control;
  }

  /**
   * 创建带三角箭头的弧形滚转按钮。
   *
   * @param startAngle 圆弧起始角。
   * @param endAngle 圆弧结束角。
   * @param arrowAtEnd 箭头是否位于结束角。
   * @returns 可直接构建按钮网格的形状几何。
   */
  private createArcArrowGeometry(
    startAngle: number,
    endAngle: number,
    arrowAtEnd: boolean,
  ): ShapeGeometry {
    const segmentCount = 12;
    const outerPoints = this.createArcPoints(
      ViewportGizmoOuterControls.ARC_OUTER_RADIUS,
      startAngle,
      endAngle,
      segmentCount,
    );
    const innerPoints = this.createArcPoints(
      ViewportGizmoOuterControls.ARC_INNER_RADIUS,
      endAngle,
      startAngle,
      segmentCount,
    );
    const arrowAngle = arrowAtEnd ? endAngle : startAngle;
    const direction = arrowAtEnd ? 1 : -1;
    const radial = new Vector2(Math.cos(arrowAngle), Math.sin(arrowAngle));
    const tangent = new Vector2(
      -Math.sin(arrowAngle) * direction,
      Math.cos(arrowAngle) * direction,
    );
    const center = radial.clone().multiplyScalar(
      (ViewportGizmoOuterControls.ARC_INNER_RADIUS
        + ViewportGizmoOuterControls.ARC_OUTER_RADIUS) / 2,
    );
    const arrowTip = center.clone().addScaledVector(tangent, 8);
    const arrowOuter = center.clone().addScaledVector(radial, 7);
    const arrowInner = center.clone().addScaledVector(radial, -7);
    const boundary = arrowAtEnd
      ? [...outerPoints, arrowOuter, arrowTip, arrowInner, ...innerPoints]
      : [...outerPoints, ...innerPoints, arrowInner, arrowTip, arrowOuter];
    const first = boundary[0];

    if (!first) {
      throw new Error('视口方向滚转按钮缺少边界点。');
    }

    const shape = new Shape().moveTo(first.x, first.y);
    for (const point of boundary.slice(1)) {
      shape.lineTo(point.x, point.y);
    }
    shape.closePath();
    return new ShapeGeometry(shape);
  }

  /**
   * 创建指定半径和角度范围的圆弧采样点。
   *
   * @param radius 圆弧半径。
   * @param startAngle 圆弧起始角。
   * @param endAngle 圆弧结束角。
   * @param segmentCount 圆弧分段数。
   * @returns 按圆弧方向排列的采样点。
   */
  private createArcPoints(
    radius: number,
    startAngle: number,
    endAngle: number,
    segmentCount: number,
  ): Vector2[] {
    return Array.from({ length: segmentCount + 1 }, (_, index) => {
      const ratio = index / segmentCount;
      const angle = startAngle + (endAngle - startAngle) * ratio;
      return new Vector2(Math.cos(angle) * radius, Math.sin(angle) * radius);
    });
  }
}

